import { useCallback, useMemo } from 'react';
import { useGetMovementsData } from '@/common/integrations/menhir/employee/movements/movements.hooks';
import {
  IGetMovementsParams,
  IMovement,
} from '@/common/integrations/menhir/employee/movements/movements.types';
import { useGetPockets } from '@/common/integrations/menhir/employee/pockets/pockets.hooks';
import { IBenefitType, IPocketType } from '@/common/types/general';

export interface IMovementsParams
  extends Pick<IGetMovementsParams, 'from' | 'to'> {
  pocketType?: IPocketType;
  category?: IBenefitType | 'budgets';
  subcategory?: IMovement['subcategory'];
  pocketIds?: string[];
  perPage?: number | 'all';
  page?: number;
  enabled?: boolean;
}

export function useMovements({
  pocketType,
  category,
  subcategory,
  pocketIds,
  perPage = 'all',
  page,
  from,
  to,
}: IMovementsParams = {}) {
  const {
    mealPocket,
    benefitsPocket,
    refresh: refreshPockets,
    isLoading: isLoadingPockets,
  } = useGetPockets();

  const pocket = useMemo(() => {
    switch (pocketType) {
      case 'meals':
        return mealPocket;
      case 'benefits':
        return benefitsPocket;
      default:
        return;
    }
  }, [pocketType, mealPocket, benefitsPocket]);

  const {
    movements,
    pendingOrders,
    refetch: refetchMovements,
    isLoading: isLoadingMovements,
  } = useGetMovementsData(
    {
      pocketId: pocket?.id,
      pocketIds,
      category,
      page,
      subcategory,
      subcategory_not_in: [
        'budget_expiration',
        'budget_reset',
        'budget_removal',
      ],
      perPage: perPage === 'all' ? undefined : perPage,
      pagination: perPage === 'all' ? 'no' : undefined,
      from,
      to,
    },
    {
      enabled: !isLoadingPockets,
    }
  );

  const refreshMovements = useCallback(() => {
    refetchMovements();
    refreshPockets();
  }, [refetchMovements, refreshPockets]);

  return {
    isLoading: isLoadingPockets || isLoadingMovements,
    movements,
    pendingOrders,
    refreshMovements,
  };
}

export const useBudgetMovements = ({
  byPocketId = false,
  pocketIds,
  subcategory,
  page = 1,
  perPage,
}: {
  byPocketId?: boolean;
  pocketIds?: string[];
  subcategory?: IMovement['subcategory'];
  page?: number;
  perPage?: number;
} = {}) => {
  const { movements, ...rest } = useGetMovementsData(
    {
      pocketIds,
      category: 'budgets',
      subcategory_not_in: subcategory
        ? undefined
        : [
            'budget_expiration',
            'budget_reset',
            'budget_removal',
            'ewa_request',
          ],
      subcategory: subcategory,
      pagination: perPage ? undefined : 'no',
      page,
      perPage,
    },
    { enabled: Boolean(pocketIds || !byPocketId) }
  );

  return {
    movements,
    ...rest,
  };
};
